import {
  reduce,
  orderBy,
  map,
  range,
  flatten,
  keys,
  filter,
  find,
  groupBy,
  snakeCase,
  indexOf,
} from "lodash";
import { DraggableItemProps } from "@/shared/widgets/Draggable/DraggableItem";
import { PriorityRankingItem } from "./types";

export interface DraggableItem extends DraggableItemProps {
  data: PriorityRankingItem;
}

export const SOURCE_COLUMN_ID = "source_column";

export const PRIORITY_COLUMN_TITLES = [
  "Must have",
  "Important to have",
  "Good to have",
];

export const PRIORITY_GROUPS_IDS = map(PRIORITY_COLUMN_TITLES, (title) =>
  snakeCase(title)
);

export const PRIORITY_COLUMN_PATTERN = /priority_column_(\d+)_(\d+)/;

// Helper function to generate priority column ID
export const createPriorityColumnId = (
  priorityIndex: number,
  slotIndex: number
): string => `priority_column_${priorityIndex}_${slotIndex}`;

// Helper function to ensure minimum slots for a priority section
export const ensureMinimumSlots = (
  state: Record<string, DraggableItem[]>,
  priorityIndex: number,
  minSlots: number = 3
) => {
  const prioritySectionKeys = keys(state).filter((key) =>
    key.startsWith(`priority_column_${priorityIndex}_`)
  );

  const currentSlots = prioritySectionKeys.length;

  if (currentSlots < minSlots) {
    for (let i = currentSlots; i < minSlots; i++) {
      const newColumnId = createPriorityColumnId(priorityIndex, i);
      if (!state[newColumnId]) {
        state[newColumnId] = [];
      }
    }
  }

  // Count filled slots in this priority section
  const filledSlots = prioritySectionKeys.filter(
    (key) => state[key] && state[key].length > 0
  ).length;

  // If all current slots are filled, add one more empty slot
  if (filledSlots >= currentSlots) {
    const newColumnId = createPriorityColumnId(priorityIndex, currentSlots);
    if (!state[newColumnId]) {
      state[newColumnId] = [];
    }
  }

  return state;
};

export const cleanupState = (
  state: Record<string, DraggableItem[]>,
  sourceColumnId: string = SOURCE_COLUMN_ID
) => {
  const { [sourceColumnId]: sourceColumn, ...priorityColumns } = state;

  // Group priority columns by priority section
  const prioritySections = reduce(
    priorityColumns,
    (acc, items, columnId) => {
      const match = columnId.match(PRIORITY_COLUMN_PATTERN);
      if (match) {
        const priorityIndex = parseInt(match[1]);
        const itemIndex = parseInt(match[2]);

        if (!acc[priorityIndex]) {
          acc[priorityIndex] = [];
        }

        // Only add columns that have items or are within the first 3 slots
        if (items.length > 0 || itemIndex < 3) {
          acc[priorityIndex].push({
            columnId,
            itemIndex,
            items,
          });
        }
      }
      return acc;
    },
    {} as Record<
      number,
      Array<{ columnId: string; itemIndex: number; items: DraggableItem[] }>
    >
  );

  // Reorganize each priority section
  const cleanedPriorityColumns = reduce(
    prioritySections,
    (acc, sections, priorityIndex) => {
      const priorityIndexNum = parseInt(priorityIndex as string);
      // Sort sections by itemIndex
      const sortedSections = orderBy(sections, "itemIndex", "asc");

      // Collect all items from this priority section
      const allItems = flatten(map(sortedSections, "items"));

      // Ensure minimum 3 slots, add more if needed
      const minSlots = 3;
      const totalSlots = Math.max(minSlots, allItems.length);

      // If all slots would be filled, add one more empty slot
      const finalSlots =
        allItems.length >= totalSlots ? totalSlots + 1 : totalSlots;

      // Redistribute items linearly
      const newSections = map(range(finalSlots), (index) => {
        const item = allItems[index];
        const newColumnId = createPriorityColumnId(priorityIndexNum, index);

        return {
          [newColumnId]: item
            ? [
                {
                  ...item,
                  columnId: newColumnId,
                  order: index,
                },
              ]
            : [],
        };
      });

      return {
        ...acc,
        ...reduce(
          newSections,
          (sectionAcc, section) => ({ ...sectionAcc, ...section }),
          {}
        ),
      };
    },
    {}
  );

  return {
    [sourceColumnId]: sourceColumn || [],
    ...cleanedPriorityColumns,
  };
};

/**
 * Create source column items from PriorityRankingItems
 * Filters out items that are already in existingItems
 */
export const createSourceItems = (
  sourceItems: PriorityRankingItem[],
  existingItems: PriorityRankingItem[],
  sourceColumnId: string = SOURCE_COLUMN_ID
): DraggableItem[] =>
  map(
    filter(sourceItems, (item) => {
      // Check by dimension ID first (preferred)
      const matchByDimId = find(
        existingItems,
        (c) => c.dimension?.id === item.dimension?.id
      );
      if (matchByDimId) return false;

      // Fallback to title matching for backwards compatibility
      const matchByTitle = find(existingItems, (c) => c.title === item.title);
      return !matchByTitle;
    }),
    (item, index) => ({
      id: item.id?.toString() ?? `source_${index}`,
      columnId: sourceColumnId,
      order: index,
      data: {
        ...item,
        order: index,
      },
      children: null as any,
    })
  );

/**
 * Create priority column items from existing PriorityRankingItems
 * Groups items by their groupTitle into priority columns
 */
export const createPriorityItems = (
  existingItems: PriorityRankingItem[],
  priorityColumnTitles: string[] = PRIORITY_COLUMN_TITLES
): Record<string, DraggableItem[]> => {
  const mappedItemsGroups = groupBy(existingItems, "groupTitle");

  return reduce(
    mappedItemsGroups,
    (acc, value, key) => {
      const priorityIndex = Math.max(
        0,
        indexOf(PRIORITY_GROUPS_IDS, snakeCase(key || ""))
      ) as number;

      // Ensure minimum 3 slots per priority section
      const minSlots = 3;
      const totalSlots = Math.max(minSlots, value.length);

      // If all slots would be filled, add one more empty slot
      const finalSlots =
        value.length >= totalSlots ? totalSlots + 1 : totalSlots;

      const columnsWithItems = reduce(
        range(finalSlots),
        (iacc, index) => {
          const item = value[index];
          const columnId = createPriorityColumnId(priorityIndex, index);

          if (item) {
            return {
              ...iacc,
              [columnId]: [
                {
                  id: item.id?.toString() ?? `${key}-${index}`,
                  columnId,
                  order: index,
                  data: {
                    ...item,
                    type: item.type || "item",
                  },
                },
              ],
            };
          }
          // Empty slot
          return {
            ...iacc,
            [columnId]: [],
          };
        },
        {}
      );

      return {
        ...acc,
        ...columnsWithItems,
      };
    },
    {}
  );
};

/**
 * Main function to create initial kanban state from PriorityRankingItems
 *
 * @param sourceItems - Available items to choose from (pool)
 * @param existingItems - Already prioritized items
 * @param sourceColumnId - ID for the source column
 * @param priorityColumnTitles - Titles for priority groups
 */
export const createInitialItems = (
  sourceItems: PriorityRankingItem[],
  existingItems: PriorityRankingItem[],
  sourceColumnId: string = SOURCE_COLUMN_ID,
  priorityColumnTitles: string[] = PRIORITY_COLUMN_TITLES
): Record<string, DraggableItem[]> => {
  const sourceColumnItems = createSourceItems(
    sourceItems,
    existingItems,
    sourceColumnId
  );
  const priorityItems = createPriorityItems(
    existingItems,
    priorityColumnTitles
  );

  // Ensure minimum slots for each priority section
  const result = {
    [sourceColumnId]: sourceColumnItems,
    ...priorityItems,
  };

  // Apply ensureMinimumSlots to each priority section
  priorityColumnTitles.forEach((_, priorityIndex) => {
    ensureMinimumSlots(result, priorityIndex, 3);
  });

  return result;
};
